// The ONE editable criteria card — shared by every designer that authors a
// criterion with the `CriteriaRule` shape:
//
//   • Visual designer   — SQL mode (one card per `?.placeholder`) and schema
//                         mode (one card per dropped column, nestable in groups)
//   • Report designer   — one card per JRXML `$P{}` parameter
//
// It owns the whole vocabulary: operator, data type, how the value is supplied
// (run-time prompt / fixed literal / system variable), BETWEEN with two
// independently-sourced bounds, IN chip lists, token-aware defaults, and the
// per-criterion fixed dropdown + F2 lookup + item_change rule (delegated to
// CriteriaLookupItemChange, the same editors the transaction designer uses).
//
// Host differences are props, never forks:
//   sqlMode     — the SQL's `?.var` IS the prompt: no operator, no compare-with.
//   promptOnly  — the criterion always prompts (a report binds its value to a
//                 JRXML parameter at run time), so the compare-with selector and
//                 the per-bound BETWEEN editor are hidden, but the operator and
//                 everything else stay.
//   headerLabel / headerBadge / tone / lockedHint — the host names the criterion
//                 (`?.from_date` vs `$P{FROM_DATE}`) and flags its own problems.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'

import { ChipInput } from '@twasta/page-runtime/txn'
import type { ProjectVariable } from '@/api/projectVariables'
import type { CriteriaOperand, CriteriaRule } from './types'
import CriteriaDefaultField from './CriteriaDefaultField'
import CriteriaLookupItemChange from './CriteriaLookupItemChange'
import CriteriaValidationsEditor from './CriteriaValidationsEditor'
import VariableTokenSelect from './VariableTokenSelect'
import { criteriaDateIso } from './helpers'
import { FIXED_VALUE, compareWithOf, type CompareWith } from './systemVariables'

export const CRITERIA_OPERATOR_OPTIONS =
  ['=', '!=', '>', '>=', '<', '<=', 'between', 'in', 'like'] as const
export const CRITERIA_TYPE_OPTIONS = ['string', 'number', 'date'] as const

/** SQL / identifier font per spec §0.3. */
const MONO_FONT = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace)"

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', fontSize: 15.5,
  border: '1px solid var(--color-border)', borderRadius: 8,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const selectStyle: React.CSSProperties = { ...inputStyle }
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4,
}
const hintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 7, lineHeight: 1.55,
}

// ── Collapsed-card summary: "operator + how the value is supplied" ──
export function operandSummary(op: CriteriaOperand): string {
  const src = op.source || 'prompt'
  if (src === 'variable') {
    const t = (op.customValue || '').trim()
    return t ? (t.startsWith(':') ? t : `@${t}`) : '@…'
  }
  if (src === 'literal') return (op.value || '').trim() || '…'
  return `ask “${(op.promptLabel || '').trim() || '…'}”`
}

export function criteriaSummary(
  rule: CriteriaRule, sqlMode?: boolean, promptOnly?: boolean,
): string {
  const op = (rule.operator || '=').toLowerCase()
  if (!sqlMode && !promptOnly && op === 'between'
      && Array.isArray(rule.operands) && rule.operands.length === 2) {
    return `between ${operandSummary(rule.operands[0])} and ${operandSummary(rule.operands[1])}`
  }
  const cw: CompareWith = sqlMode || promptOnly ? 'prompt' : compareWithOf(rule)
  if (cw === 'variable') {
    const t = (rule.customValue || '').trim()
    return `${op} ${t.startsWith(':') ? t : `@${t}`}`
  }
  if (cw === 'literal') {
    const chips = Array.isArray(rule.chipValue) ? rule.chipValue.map(String).filter(Boolean) : []
    if (op === 'in') return `in (${chips.join(', ') || '…'})`
    if (op === 'between') return `between ${chips[0] || '…'} and ${chips[1] || '…'}`
    return `${op} ${(rule.value || '').trim() || '…'}`
  }
  const label = (rule.promptLabel || rule.field || '').trim()
  const dflt = (rule.value || '').trim()
  return `${op} ask “${label || '…'}”${dflt ? ` · default ${dflt}` : ''}`
}

// ── A single editable criteria card ───────────────────────────────
export default function CriteriaCard({
  rule, idx, project, allRules, fieldLocked, sqlMode, promptOnly, projectVars,
  defaultOpen, expandSignal, headerLabel, headerBadge, tone, lockedHint,
  triggerName, otherParamNames: otherParamNamesProp, lookupTitleMaxLength,
  onChange, onSqlBinding, onDelete,
}: {
  rule: CriteriaRule
  idx: number
  project: string
  allRules: CriteriaRule[]
  fieldLocked?: boolean
  sqlMode?: boolean
  /** The value always comes from a run-time prompt (the Report designer: the
   *  criterion binds a JRXML `$P{}` parameter, which the user fills before the
   *  report runs). Hides the compare-with selector and the per-bound BETWEEN
   *  editor; the operator, type, default and lookup/item_change stay. */
  promptOnly?: boolean
  projectVars: ProjectVariable[]
  /** Freshly-added criteria mount EXPANDED; existing ones start collapsed. */
  defaultOpen?: boolean
  /** Expand/collapse-ALL broadcast — each seq bump applies `open` to every
   *  card (a card mounted after the bump keeps its own default). */
  expandSignal?: { open: boolean; seq: number }
  /** Header identity. Defaults to the rule's `field`. */
  headerLabel?: React.ReactNode
  /** Extra header content (e.g. a "not declared in the JRXML" warning). */
  headerBadge?: React.ReactNode
  /** 'warn' outlines the card — the host flagging its own problem. */
  tone?: 'normal' | 'warn'
  /** Footer note shown when `fieldLocked`. Defaults to the SQL binding hint. */
  lockedHint?: React.ReactNode
  /** Canonical param name for lookup/item_change. Defaults to the rule's own. */
  triggerName?: string
  /** The OTHER criteria's param names (fill targets / {form.X} tokens).
   *  Defaults to the canonicalised names of `allRules`. */
  otherParamNames?: string[]
  /** Longest lookup title the host stores. Absent ⇒ no limit. */
  lookupTitleMaxLength?: number
  onChange: (idx: number, field: keyof CriteriaRule, value: unknown) => void
  onSqlBinding?: (varName: string, type: 'string' | 'number' | 'date' | undefined, defaultValue: string | undefined) => void
  onDelete?: () => void
}) {
  const [open, setOpen] = useState(!!defaultOpen)
  const seenSeqRef = useRef(expandSignal?.seq ?? 0)
  useEffect(() => {
    const seq = expandSignal?.seq ?? 0
    if (seq !== seenSeqRef.current) {
      seenSeqRef.current = seq
      if (expandSignal) setOpen(expandSignal.open)
    }
  }, [expandSignal])
  const ruleType = (rule.COLTYPE || 'string') as 'string' | 'number' | 'date'
  // Canonical UPPER_SNAKE param name (mirror of backend _norm_param) — the key
  // the runtime criteria form + item_change use, so fill targets match.
  const canon = (s: string) => (s || '').trim().replace(/\s+/g, '_').toUpperCase()
  const otherParamNames = otherParamNamesProp ?? allRules
    .filter((_, j) => j !== idx)
    .map((r) => canon(r.field || r.promptLabel || ''))
    .filter(Boolean)
  // How the value is supplied. SQL mode is prompt-only — the ?.var placeholder
  // IS a prompt (a literal is typed straight into the SQL, a system value is
  // ?.@Token), so the selector only appears in schema mode.
  const compareWith: CompareWith =
    sqlMode || promptOnly ? 'prompt' : compareWithOf(rule)
  const setCompareWith = (next: CompareWith) => {
    if (next === 'prompt') {
      onChange(idx, 'queryOption', 'P')
      onChange(idx, 'prevOption', 'P')
      onChange(idx, 'customValue', FIXED_VALUE)
    } else if (next === 'literal') {
      onChange(idx, 'queryOption', 'F')
      onChange(idx, 'prevOption', 'F')
      onChange(idx, 'customValue', FIXED_VALUE)
    } else {
      onChange(idx, 'queryOption', 'F')
      onChange(idx, 'prevOption', 'F')
      // Pre-pick a sensible token so the rule is never F + empty customValue
      // (which the engine reads as an empty literal).
      onChange(idx, 'customValue', ruleType === 'date' ? '1stDayOfMonth' : 'LoginUser')
      // An IN literal list makes no sense against ONE variable — snap back to
      // a scalar comparison. (BETWEEN is fine: each bound has its own source.)
      if ((rule.operator || '=').toLowerCase() === 'in') {
        onChange(idx, 'operator', '=')
      }
    }
  }
  const valueInputType = ruleType === 'date' ? 'date' : ruleType === 'number' ? 'number' : 'text'
  const shownValue = (v: string | undefined) => (ruleType === 'date' ? criteriaDateIso(v || '') : v || '')
  const chips = Array.isArray(rule.chipValue) ? rule.chipValue : []
  const operatorLc = (rule.operator || '=').toLowerCase()

  // ── BETWEEN — two independently-sourced bounds (schema mode) ──
  // The rule's `operands` [lo, hi] each carry their OWN source, so a range
  // like TRAN_DATE >= <prompt> AND <= <fixed date> is ONE criterion. A legacy
  // between (single source + chipValue pair) synthesizes a starting pair and
  // migrates to `operands` on first edit.
  //
  // promptOnly hosts stay OUT of this path: a report folds ONE criterion onto
  // ONE `$P{}` parameter, so a two-bound criterion would leave the second
  // bound unbindable — a report range is authored as two parameters instead.
  const isBetween = !sqlMode && !promptOnly && operatorLc === 'between'
  const baseLabel = rule.promptLabel || rule.field
  const synthesizedOperands: [CriteriaOperand, CriteriaOperand] = useMemo(() => {
    const o = rule.operands
    if (Array.isArray(o) && o.length === 2) {
      return [o[0] || {}, o[1] || {}] as [CriteriaOperand, CriteriaOperand]
    }
    const src: CriteriaOperand['source'] =
      compareWith === 'variable' ? 'variable'
        : compareWith === 'literal' ? 'literal' : 'prompt'
    return [
      { source: src, value: String(chips[0] ?? rule.value ?? ''), customValue: '', promptLabel: `${baseLabel} From` },
      { source: src, value: String(chips[1] ?? ''), customValue: '', promptLabel: `${baseLabel} To` },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.operands, compareWith, baseLabel, JSON.stringify(chips), rule.value])
  const setOperand = (pos: 0 | 1, patch: Partial<CriteriaOperand>) => {
    const next: [CriteriaOperand, CriteriaOperand] = [
      { ...synthesizedOperands[0] }, { ...synthesizedOperands[1] },
    ]
    next[pos] = { ...next[pos], ...patch }
    onChange(idx, 'operands', next)
  }
  const handleOperatorChange = (op: string) => {
    onChange(idx, 'operator', op)
    if (!sqlMode && !promptOnly && op.toLowerCase() === 'between') {
      // Materialize the pair so the engine takes the first-class path.
      if (!Array.isArray(rule.operands) || rule.operands.length !== 2) {
        onChange(idx, 'operands', synthesizedOperands)
      }
    } else if (rule.operands) {
      onChange(idx, 'operands', undefined)
    }
  }
  return (
    <div style={{
      border: `1px solid ${tone === 'warn' ? 'var(--color-warning, #b58900)' : 'var(--color-border)'}`,
      borderRadius: 10, padding: 10,
      marginBottom: 8, background: 'var(--color-bg)',
      alignSelf: 'start',
    }}>
      {/* Header — always visible; click to collapse/expand. Collapsed, the
          card shows a one-line summary of the condition. */}
      <div
        onClick={() => setOpen((o) => !o)}
        title={open ? 'Collapse — the summary stays visible' : 'Expand to edit this filter'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: open ? 8 : 0, cursor: 'pointer', minWidth: 0,
        }}
      >
        {open
          ? <ChevronDown size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
          : <ChevronRight size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />}
        <span style={{
          fontFamily: MONO_FONT, fontSize: 13, fontWeight: 600,
          color: 'var(--color-text-secondary)',
          flexShrink: 1, minWidth: 60, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {headerLabel ?? (rule.field || '(field)')}
        </span>
        {headerBadge}
        {!open && (
          <span style={{
            flex: 1, minWidth: 0, fontSize: 13, color: 'var(--color-text)',
            padding: '1px 8px', borderRadius: 6,
            background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={criteriaSummary(rule, sqlMode, promptOnly)}>
            {criteriaSummary(rule, sqlMode, promptOnly)}
          </span>
        )}
        {open && <span style={{ flex: 1 }} />}
        {onDelete && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            title="Remove filter"
            style={{ padding: '2px 4px', flexShrink: 0 }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {open && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Operator only in schema mode — in SQL mode it's written in the query. */}
          {!sqlMode && (
            <select
              value={rule.operator || '='}
              onChange={(e) => handleOperatorChange(e.target.value)}
              style={{ ...selectStyle, flex: 1 }}
              title="Operator"
            >
              {CRITERIA_OPERATOR_OPTIONS
                // One variable can't feed an IN list (between is fine — each
                // bound picks its own source below).
                .filter((op) => compareWith !== 'variable' || op !== 'in')
                .map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
          )}
          <select
            value={ruleType}
            onChange={(e) => {
              const t = e.target.value as 'string' | 'number' | 'date'
              if (sqlMode && onSqlBinding) onSqlBinding(rule.field, t, rule.value)
              else { onChange(idx, 'COLTYPE', t); onChange(idx, 'currentFieldType', t) }
            }}
            style={{ ...selectStyle, flex: 1 }}
            title="Data type"
          >
            {CRITERIA_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {/* BETWEEN — each bound picks its OWN source (prompt / fixed /
            variable), so mixed ranges like `>= <prompt> AND <= @Today` are one
            criterion. Replaces the single compare-with selector below. */}
        {isBetween && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {([0, 1] as const).map((pos) => {
              const op = synthesizedOperands[pos]
              const src = op.source || 'prompt'
              return (
                <div key={pos} style={{
                  border: '1px dashed var(--color-border)', borderRadius: 8,
                  padding: 8, minWidth: 0, background: 'var(--color-bg-secondary)',
                }}>
                  <div style={{ ...fieldLabelStyle, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {pos === 0 ? 'From' : 'To'}
                  </div>
                  <select
                    value={src}
                    onChange={(e) => {
                      const source = e.target.value as CriteriaOperand['source']
                      setOperand(pos, {
                        source,
                        // Never leave a variable operand with an empty token —
                        // the engine would read it as an empty literal.
                        ...(source === 'variable' && !(op.customValue || '').trim()
                          ? { customValue: ruleType === 'date' ? (pos === 0 ? '1stDayOfMonth' : 'Today') : 'LoginUser' }
                          : {}),
                      })
                    }}
                    style={{ ...selectStyle, width: '100%', marginBottom: 6 }}
                    title="How this bound is supplied"
                  >
                    <option value="prompt">Ask the user (prompt)</option>
                    <option value="literal">Fixed value</option>
                    <option value="variable">System variable</option>
                  </select>
                  {src === 'prompt' && (
                    <>
                      <div style={fieldLabelStyle}>Prompt heading</div>
                      <input
                        type="text"
                        value={op.promptLabel || ''}
                        onChange={(e) => setOperand(pos, { promptLabel: e.target.value })}
                        placeholder={pos === 0 ? 'e.g. From Date' : 'e.g. To Date'}
                        style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                      />
                      <div style={fieldLabelStyle}>Default value</div>
                      <CriteriaDefaultField
                        value={op.value || ''}
                        projectVars={projectVars}
                        onChange={(val) => setOperand(pos, { value: val })}
                        inputStyle={{ ...inputStyle, width: '100%' }}
                        selectStyle={{ ...selectStyle, width: '100%' }}
                      />
                    </>
                  )}
                  {src === 'literal' && (
                    <>
                      <div style={fieldLabelStyle}>Value</div>
                      <input
                        type={valueInputType}
                        value={shownValue(op.value)}
                        onChange={(e) => setOperand(pos, { value: e.target.value })}
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    </>
                  )}
                  {src === 'variable' && (
                    <>
                      <div style={fieldLabelStyle}>Variable</div>
                      <VariableTokenSelect
                        value={op.customValue || ''}
                        projectVars={projectVars}
                        onChange={(t) => setOperand(pos, { customValue: t })}
                        selectStyle={{ ...selectStyle, width: '100%' }}
                      />
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {/* Compare-with sits where the value goes: pick HOW the right-hand
            side is supplied, then the mode's own fields appear below. SQL mode
            is prompt-only (a literal is typed in the SQL, a variable is
            ?.@Token), so the selector is hidden there. BETWEEN uses the
            per-bound sources above instead. */}
        {!sqlMode && !promptOnly && !isBetween && (
          <select
            value={compareWith}
            onChange={(e) => setCompareWith(e.target.value as CompareWith)}
            style={selectStyle}
            title="How the comparison value is supplied"
          >
            <option value="prompt">Ask the user (prompt)</option>
            <option value="literal">Fixed value</option>
            <option value="variable">System variable</option>
          </select>
        )}
        {!isBetween && compareWith === 'prompt' && (
          <>
            <div>
              <div style={fieldLabelStyle}>Prompt heading</div>
              <input
                type="text"
                value={rule.promptLabel || ''}
                onChange={(e) => onChange(idx, 'promptLabel', e.target.value)}
                placeholder="e.g. From Date"
                style={{ ...inputStyle, width: '100%' }}
              />
            </div>
            <div>
              <div style={fieldLabelStyle}>Default value</div>
              <CriteriaDefaultField
                value={shownValue(rule.value)}
                projectVars={projectVars}
                // IN prompts default to a LIST — chip entry, stored
                // comma-joined (mirrored into chipValue below).
                chips={operatorLc === 'in'}
                chipInputType={valueInputType}
                onChange={(val) => {
                  if (sqlMode && onSqlBinding) onSqlBinding(rule.field, ruleType, val)
                  else {
                    onChange(idx, 'value', val)
                    if (operatorLc === 'in') {
                      onChange(idx, 'chipValue',
                        val.split(',').map((s) => s.trim()).filter(Boolean))
                    }
                  }
                }}
                inputStyle={{ ...inputStyle, width: '100%' }}
                selectStyle={{ ...selectStyle, width: '100%' }}
              />
            </div>
          </>
        )}
        {!isBetween && compareWith === 'literal' && (
          <div>
            <div style={fieldLabelStyle}>{operatorLc === 'in' ? 'Values' : 'Value'}</div>
            {operatorLc === 'in' ? (
              // Chip-style list entry — each chip is one IN value. The engine
              // reads the list from chipValue; value keeps the comma-joined
              // mirror for older readers.
              <ChipInput
                values={chips.map((c) => String(c)).filter(Boolean)}
                onChange={(list) => {
                  onChange(idx, 'value', list.join(','))
                  onChange(idx, 'chipValue', list)
                }}
                inputType={valueInputType}
                placeholder="Add value…"
              />
            ) : (
              <input
                type={valueInputType}
                value={shownValue(rule.value)}
                onChange={(e) => onChange(idx, 'value', e.target.value)}
                placeholder="e.g. AP"
                style={{ ...inputStyle, width: '100%' }}
              />
            )}
          </div>
        )}
        {!isBetween && compareWith === 'variable' && (
          <div>
            <div style={fieldLabelStyle}>Variable</div>
            {/* Same token vocabulary as the default-value editor: dates,
                current-user AND project variables. */}
            <VariableTokenSelect
              value={rule.customValue || ''}
              projectVars={projectVars}
              onChange={(t) => onChange(idx, 'customValue', t)}
              selectStyle={{ ...selectStyle, width: '100%' }}
            />
          </div>
        )}
        {/* Lookup + item_change apply to any prompt on this column — the
            single prompt, or either prompt-sourced bound of a BETWEEN. */}
        {((!isBetween && compareWith === 'prompt')
          || (isBetween && synthesizedOperands.some((o) => (o.source || 'prompt') === 'prompt'))) && (
          <CriteriaLookupItemChange
            project={project}
            triggerName={triggerName ?? canon(rule.field || rule.promptLabel || '')}
            otherParamNames={otherParamNames}
            value={{
              lookup: rule.lookup,
              lookup_filter: rule.lookup_filter,
              lookup_filter_rules: rule.lookup_filter_rules,
              lookup_sql: rule.lookup_sql,
              lookup_title: rule.lookup_title,
              lookup_sql_data_col: rule.lookup_sql_data_col,
              item_change: rule.item_change,
              options: rule.options,
            }}
            onLookupChange={(patch) => {
              for (const [k, v] of Object.entries(patch)) {
                onChange(idx, k as keyof CriteriaRule, v)
              }
            }}
            onItemChange={(v) => onChange(idx, 'item_change', v ?? null)}
            onOptionsChange={(v) => onChange(idx, 'options', v ?? [])}
            lookupTitleMaxLength={lookupTitleMaxLength}
          />
        )}
        {/* Checks on the value the user gives this prompt — run by the server
            before the run (a prompt with no default is already required). */}
        {!isBetween && compareWith === 'prompt' && (
          <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              Checks
            </div>
            <CriteriaValidationsEditor
              project={project}
              label={rule.promptLabel || rule.field}
              criteriaNames={[triggerName ?? canon(rule.field || rule.promptLabel || ''), ...otherParamNames]
                .filter(Boolean).map((n) => n.toLowerCase())}
              validations={rule.validations}
              onChange={(next) => onChange(idx, 'validations', next.length ? next : undefined)}
            />
          </div>
        )}
      </div>
      )}
      {open && fieldLocked && (
        <div style={{ ...hintStyle, marginTop: 4 }}>
          {lockedHint ?? (
            <>Bound to <code>?.{(rule.field || '').toLowerCase()}</code> in the SQL.</>
          )}
        </div>
      )}
    </div>
  )
}
